import { useEffect, useState, useCallback } from 'react';
import { Search, Check } from 'lucide-react';
import { supabase } from '../../supabaseClient';

function money(cents) {
  return `$${(cents / 100).toFixed(2)}`;
}

// Dumb-on-purpose token overlap between this product's name and a request's
// text. Words of 3+ chars only. This is the v1 "match" — good enough to
// surface candidates for the admin to eyeball. The real ~70% fuzzy scoring is
// a later layer (see PLAN.md); swapping this function is the whole change.
function scoreMatch(productName, req) {
  const norm = (s) => (s || '').toLowerCase().replace(/[^a-z0-9\s]/g, ' ');
  const prodWords = new Set(norm(productName).split(/\s+/).filter((w) => w.length >= 3));
  const reqWords = norm(`${req.card_name || ''} ${req.set_name || ''} ${req.raw_text || ''}`)
    .split(/\s+/)
    .filter((w) => w.length >= 3);
  let hits = 0;
  for (const w of new Set(reqWords)) if (prodWords.has(w)) hits += 1;
  return hits;
}

// Shows open customer "looking for" requests that may match the product being
// edited, and lets the admin mark one as found. Marking flips the request to
// status='matched' + links this product; the customer sees it in their
// Requests tab immediately.
//
// STUB: the customer is NOT emailed yet — that send needs Resend (see
// PLAN.md). notified_at stays null so a future Resend job can pick these up.
export default function RequestMatchPanel({ productId, productName }) {
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    supabase
      .from('want_list_items')
      .select('id, raw_text, card_name, set_name, condition, max_price_cents, status')
      .eq('status', 'open')
      .then(({ data }) => {
        const scored = (data || [])
          .map((r) => ({ ...r, score: scoreMatch(productName, r) }))
          .filter((r) => r.score > 0)
          .sort((a, b) => b.score - a.score);
        setMatches(scored);
        setLoading(false);
      });
  }, [productName]);

  useEffect(() => { if (productName) load(); }, [load, productName]);

  const markFound = async (reqId) => {
    setBusyId(reqId);
    await supabase
      .from('want_list_items')
      .update({
        status: 'matched',
        matched_product_id: productId,
        matched_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      })
      .eq('id', reqId);
    setBusyId(null);
    setMatches((prev) => prev.filter((r) => r.id !== reqId));
  };

  return (
    <div className="sv-card" style={{ padding: 16, marginBottom: 20 }}>
      <label style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: '0.72rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase' }}>
        <Search size={14} /> Customer requests that may match
      </label>

      {loading ? (
        <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', margin: '10px 0 0' }}>Checking…</p>
      ) : matches.length === 0 ? (
        <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', margin: '10px 0 0' }}>
          No open requests look like this product.
        </p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 10 }}>
          {matches.map((r) => (
            <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 10, justifyContent: 'space-between', padding: '10px 12px', border: '1px solid var(--border)', borderRadius: 8 }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 700, fontSize: '0.88rem' }}>{r.card_name || r.raw_text}</div>
                <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: 2, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                  {r.card_name && <span>{r.raw_text}</span>}
                  {r.set_name && <span>{r.set_name}</span>}
                  {r.condition && <span>{r.condition}</span>}
                  {r.max_price_cents != null && <span>up to {money(r.max_price_cents)}</span>}
                </div>
              </div>
              <button
                type="button"
                onClick={() => markFound(r.id)}
                disabled={busyId === r.id}
                className="sv-btn"
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 12px', fontSize: '0.8rem', whiteSpace: 'nowrap' }}
              >
                <Check size={14} /> {busyId === r.id ? '…' : 'Mark found'}
              </button>
            </div>
          ))}
        </div>
      )}
      <p style={{ fontSize: '0.76rem', color: 'var(--text-muted)', marginTop: 10, marginBottom: 0 }}>
        Marking found links this product to the request; the customer sees it right away. Email to them turns on once Resend is connected (see PLAN.md).
      </p>
    </div>
  );
}
